import * as PIXI from "pixi.js";
import * as HLF from "html-living-framework";
import {CookingActivity, CookingActivityStep} from "./CookingActivity";
import {KitchenScreen} from "../../States/KitchenScreen";
import {array, Callback} from "../../Core/Reflection";
import {delightStep1, delightStep2, delightStep3, delightStep4, delightStep5, delightStep6, delightStep7} from "../../Layout/KitchenScreenLayout";
import {Sprite} from "../../Core/Sprite";
import {CookingItem} from "./CookingItem";
import {Ease} from "../../Core/Animatable";
import {CookingItemColor} from "./CookingItemColor";
import {BowlMix, CookingActivityStepMix} from "./CookingActivityStepMix";
import {Poly} from "../../Core/Shape";
import {Container} from "../../Core/Container";
import {playVO, ReactionVO} from "../../Data/VoiceOver";
import {Ingredient} from "./Ingredient";
import {Signal} from "../../Core/Signal";
import {IPoint, PointUtil} from "../../Core/Geom";
import {AudioChannel} from "../../Core/AudioChannel";
import {Keyboard} from "../Keyboard";

export class TurkishDelightActivity extends CookingActivity {

    constructor(kitchen: KitchenScreen) {
        super(kitchen, [
            TurkishDelightStep1_Ingredients,
            TurkishDelightStep2_Mix,
            TurkishDelightStep3_Bake,
            TurkishDelightStep4_Slice,
            TurkishDelightStep5_Toppings,
            TurkishDelightStep6_Box,
            TurkishDelightStep7_Eat,
        ]);
    }
}

class TurkishDelightStep1_Ingredients extends CookingActivityStep {

    readonly colorBottles = [
        "#ff7da2",
        "#bff056",
        "#da83f6"
    ].map((it, i) => this.addChild(new ColorBottle(it, i + 1)));
    readonly cuttingBoard = this.addChild(new Sprite());
    readonly bowlMix = this.addChild(new BowlMix());
    readonly ingredientSugarBowl = this.addChild(new Ingredient());
    readonly ingredientWaterJug = this.addChild(new Ingredient("#84dcfe"));
    readonly ingredientCornStarch = this.addChild(new Ingredient());
    readonly arrow = this.addChild(new Sprite());
    readonly arrowEnd = {x: 0, y: 0};

    play(onComplete: Callback, prev?: CookingActivityStep): void {
        delightStep1.call(this);

        this.arrow.visible = false;
        this.bowlMix.bowlContent.visible = false;

        const bowl = this.bowlMix;
        const ingredients = [this.ingredientCornStarch, this.ingredientWaterJug, this.ingredientSugarBowl];
        const bottles = [...this.colorBottles].sort((a, b) => b.x - a.x);
        const hoverVO = [
            "LetsGoLuna_166",
            "LetsGoLuna_168",
            "LetsGoLuna_167"
        ];
        for (const bottle of this.colorBottles) {
            bottle.hoverVO = hoverVO[bottle.colorIndex - 1];
            bottle.zIndex = 0;
            bottle.visible = false;
        }

        this.ingredientSugarBowl.hoverVO = "LetsGoLuna_163";
        this.ingredientWaterJug.hoverVO = "LetsGoLuna_164";
        this.ingredientCornStarch.hoverVO = "LetsGoLuna_165";

        for (const item of [...ingredients, ...bottles]) {
            item.target = bowl;
            item.targetTransform = {
                y: bowl.y - bowl.height * (ingredients.indexOf(item) >= 0 ? 1 : 0.5),
                rotation: 80 * Math.PI / 180
            };
        }

        const color = new CookingItemColor("delight-color-{i}", bowl.bowlContent);
        color.index = 0;

        ingredients.forEach(it => it.enabled = false);
        this.moveItemsFromRight([this.cuttingBoard, bowl]);
        this.moveItemsFromLeft(ingredients, () => {
            playVO("LetsGoLuna_162");
            this.showArrow(this.arrow, this.arrowEnd);

            let draggedIngredients = 0;
            const onDragIngredient = () => {
                this.hideArrow(this.arrow);
                draggedIngredients++;
                if (draggedIngredients === ingredients.length) {
                    showColorBottles();
                }
            };
            bowl.enabled = true;
            for (const ingredient of ingredients) {
                ingredient.enabled = true;
                ingredient.onDragged.add(() => {
                    if (ingredient === this.ingredientWaterJug) {
                        AudioChannel.SFX.play("liquid-pour");
                    }
                    ingredient.enabled = false;
                    ingredient.pour(onDragIngredient);
                    const content = bowl.bowlContent;
                    if (!content.visible) {
                        content.visible = true;
                        content.tweenFrom(0.2, Ease.sineInOut, {scaleY: 0, alpha: 0, delay: 0.8});
                    }
                });
                ingredient.onKeyboard.add(() => {
                    Keyboard.focus([...ingredients].reverse().filter(it => it.enabled)[0] || this.kitchen.buttonHome);
                });
            }
            Keyboard.focus(ingredients[ingredients.length - 1]);
        }, 0.6);

        const showColorBottles = () => {
            playVO("LetsGoLuna_171");
            for (const bottle of bottles) {
                bottle.visible = true;
                bottle.enabled = false;
            }
            this.moveItemsFromLeft(bottles, () => {
                for (const bottle of bottles) {
                    bottle.enabled = true;
                    bottle.saveTransform();
                    bottle.onDragged.add(() => {
                        bowl.enabled = false;
                        bottles.forEach(it => it.enabled = false);
                        bottle.pouringLifetime = 0.2;
                        bottle.delayedCall(0.8, () => color.index = bottle.colorIndex);
                        bottle.pour(() => {
                            this.moveItemsToLeft(bottles.reverse(), () => {
                                bowl.moveYTo(bowl.y + this.cuttingBoard.height * 0.1, 0.05, Ease.sineIn, {delay: 0.1});
                                this.moveItemsToRight(this.cuttingBoard, onComplete);
                            });
                        });
                    });
                    bottle.onKeyboard.add(() => {
                        Keyboard.focus(this.kitchen.buttonHome);
                    });
                    Keyboard.focus(bottles[0]);
                }
            });
        };

        Keyboard.add(...ingredients, ...bottles);
    }
}

class TurkishDelightStep2_Mix extends CookingActivityStepMix {

    play(onComplete: Callback, prev: TurkishDelightStep1_Ingredients): void {
        delightStep2.call(this);

        const spoon = this.spoon;
        const bowl = this.bowlMix;
        spoon.visible = false;
        bowl.bowlContent.filters = prev.bowlMix.bowlContent.filters;
        bowl.moveXYFrom(prev.bowlMix.x, prev.bowlMix.y, 0.6, Ease.sineInOut, () => {
            spoon.visible = true;
            spoon.enabled = false;
            this.moveItemsFromLeft(spoon, () => {
                this.mix(onComplete);
            });
        });
        prev.destroy();
    }
}

class TurkishDelightStep3_Bake extends CookingActivityStep {

    readonly bowlMix = this.addChild(new BowlMix());
    readonly bowlCook = this.addChild(new BowlMix());

    play(onComplete: Callback, prev: TurkishDelightStep2_Mix): void {
        delightStep3.call(this);

        const bowlMix = this.bowlMix;
        const bowlCook = this.bowlCook;
        const prevBowl = prev.bowlMix;
        const oven = this.kitchen.oven;
        const luna = this.kitchen.luna;

        bowlMix.visible = false;
        bowlCook.visible = false;

        bowlMix.bowlContent.filters = prevBowl.bowlContent.filters;
        bowlCook.bowlContent.filters = prevBowl.bowlContent.filters;

        luna.show(() => {
            playVO(["LetsGoLuna_139", "LetsGoLuna_172"], () => {
                oven.open();
                bowlMix.visible = true;
                bowlMix.tweenFrom(0.6, Ease.sineInOut, {
                    x: prevBowl.x,
                    y: prevBowl.y,
                    scaleX: prevBowl.scale.x,
                    scaleY: prevBowl.scale.y,
                    onComplete: () => {

                        const zIndex = this.kitchen.cookingActivity.zIndex;
                        this.kitchen.cookingActivity.zIndex = oven.zIndex;

                        oven.close(() => {
                            bowlMix.visible = false;

                            const timer = this.kitchen.ovenTimer;
                            timer.show(3, () => timer.hide(() => {
                                playVO("LetsGoLuna_173", () => {
                                    oven.open(() => {
                                        this.kitchen.cookingActivity.zIndex = zIndex;
                                        bowlCook.visible = true;
                                        bowlCook.tweenFrom(0.6, Ease.sineInOut, {
                                            x: bowlMix.x,
                                            y: bowlMix.y,
                                            scaleX: bowlMix.scale.x,
                                            scaleY: bowlMix.scale.y,
                                            onComplete: () => oven.close(() => luna.hide(onComplete))
                                        });
                                    });
                                    this.kitchen.playFactVO(["LetsGoLuna_178", "LetsGoLuna_179"]);
                                });
                            }));
                        });
                    }
                });
                prev.destroy();
            });
        });
    }
}

class TurkishDelightStep4_Slice extends CookingActivityStep {

    readonly bowlCook = this.addChild(new BowlMix());
    readonly jelly = this.addChild(new Jelly());
    readonly pieces = this.addChild(new Pieces());

    play(onComplete: Callback, prev?: TurkishDelightStep3_Bake): void {
        delightStep4.call(this);

        const bowl = this.bowlCook;
        const jelly = this.jelly;
        const pieces = this.pieces.pieces;

        jelly.visible = false;
        pieces.forEach(it => it.visible = false);
        if (prev) {
            const filters = prev.bowlCook.bowlContent.filters;
            this.pieces.setFilters(filters);
            jelly.setFilters(filters);
            bowl.bowlContent.filters = filters;
            prev.destroy();
        }

        bowl.pulse();
        bowl.enabled = true;
        bowl.onClick.add(() => {
            bowl.stopPulse();
            bowl.enabled = false;
            bowl.bowlContent.moveYTo(bowl.bowlContent.y - bowl.bowlFront.height, 0.6, Ease.sineInOut, () => {
                bowl.bowlContent.changeParent(this);
                bowl.bowlContent.visible = false;
                this.moveItemsToRight(bowl);
                jelly.visible = true;
                jelly.moveYFrom(bowl.bowlContent.y, 0.2, Ease.expoIn, {
                    delay: 0.2,
                    onComplete: () => {
                        jelly.tweenTo(0.15, Ease.sineInOut, {
                            scaleX: jelly.scale.x * 1.1,
                            scaleY: jelly.scale.y * 0.8,
                            repeat: 1,
                            yoyo: true,
                            onComplete: () => {
                                let piecesMoved = 0;
                                jelly.pulse();
                                jelly.enabled = true;
                                jelly.onClick.add(() => {
                                    AudioChannel.SFX.play("knife-slice");
                                    jelly.stopPulse();
                                    if (!jelly.slice()) {
                                        jelly.enabled = false;
                                        jelly.alphaTo(0, 0.1, () => jelly.visible = false);
                                        Keyboard.focus(this.kitchen.buttonHome);
                                    }
                                    const newPieces = pieces.slice(this.jelly.slices - 2, this.jelly.slices);
                                    const jellyPieces = jelly.pieces.slice(this.jelly.slices - 2, this.jelly.slices);
                                    for (let i = 0, n = Math.min(newPieces.length, jellyPieces.length); i < n; i++) {
                                        const piece = newPieces[i];
                                        const jellyPiece = jellyPieces[i];
                                        jellyPiece.changeParent(this.pieces);
                                        piece.visible = true;
                                        piece.zIndex = i;
                                        piece.alphaFrom(0, 0.1);
                                        piece.tweenFrom(0.4, Ease.sineInOut, {
                                            x: jellyPiece.x,
                                            y: jellyPiece.y,
                                            scaleX: jellyPiece.scale.x,
                                            scaleY: jellyPiece.scale.y,
                                            onComplete: () => {
                                                piecesMoved++;
                                                if (piecesMoved >= pieces.length) {
                                                    onComplete();
                                                }
                                            }
                                        });
                                        jellyPiece.changeParent(this.jelly.visuals);
                                    }
                                });
                                Keyboard.add(jelly);
                                Keyboard.focus(jelly);
                            }
                        });
                    }
                });
            });
            Keyboard.focus(this.kitchen.buttonHome);
        });
        Keyboard.add(bowl);
        Keyboard.focus(bowl);
    }
}

class TurkishDelightStep5_Toppings extends CookingActivityStep {

    readonly sugar = this.addChild(new Sprite());
    readonly sugarBag = this.addChild(new SugarBag());
    readonly pieces = this.addChild(new Pieces());
    readonly sugarDrag = new SugarDrag();

    play(onComplete: Callback, prev: TurkishDelightStep4_Slice): void {
        delightStep5.call(this);

        const bag = this.sugarBag;
        const pieces = this.pieces;
        const sugar = this.sugar;
        const drag = this.sugarDrag;

        bag.hoverVO = "LetsGoLuna_169";
        bag.enabled = false;
        sugar.visible = false;
        pieces.setFilters(prev.jelly.sprite.filters);
        pieces.moveXYFrom(prev.pieces.x, prev.pieces.y, 0.6, Ease.sineInOut);
        this.moveItemsFromRight(bag, () => {
            pieces.enabled = true;
            bag.enabled = true;
            bag.draggable = false;
            bag.pulse();
            bag.saveTransform();
            bag.target = pieces;
            bag.targetTransform = {
                y: pieces.y - pieces.height * 0.8,
                rotation: 60 * Math.PI / 180
            };
            const startDragSugar = (p: IPoint) => {
                bag.stopPulse();
                this.addChild(drag);
                drag.startDrag(p);
            };
            bag.onStartDrag.add(e => {
                startDragSugar(e.data.getLocalPosition(this));
            });
            bag.onVirtualDrag.add(data => {
                const p = data.getLocalPosition(this);
                drag.x = p.x;
                drag.y = p.y;
            });
            bag.onDragged.add(() => {
                bag.enabled = false;
                pieces.enabled = false;
                const s = 1000;
                const p = {x: pieces.x, y: pieces.y - pieces.height * 0.2};
                drag.moveXYTo(p.x, p.y, PointUtil.distance(bag, p) / s, Ease.sineInOut, () => {
                    for (const piece of pieces.pieces) {
                        piece.topping.visible = true;
                        piece.topping.alphaFrom(0, 0.2);
                    }
                    drag.alphaTo(0, 0.2);
                    sugar.visible = true;
                    sugar.alphaFrom(0, 0.2);
                    bag.tweenTo(0.4, Ease.sineInOut, {
                        ...bag.getTransformBackup(), onComplete: () => {
                            this.moveItemsToRight(bag, onComplete);
                        }
                    });
                });
            });
            bag.onKeyboard.add(() => {
                startDragSugar(bag);
                Keyboard.focus(this.kitchen.buttonHome);
            });
            Keyboard.add(bag);
            Keyboard.focus(bag);
        });
        prev.destroy();
    }
}

class TurkishDelightStep6_Box extends CookingActivityStep {

    readonly box = this.addChild(new Box());
    readonly pieces = this.addChild(new Pieces());
    readonly arrow = this.addChild(new Sprite());
    readonly arrowEnd = {x: 0, y: 0};

    play(onComplete: Callback, prev: TurkishDelightStep5_Toppings): void {
        delightStep6.call(this);

        const box = this.box;
        const pieces = this.pieces;
        const filters = prev.pieces.pieces[0].sprite.filters;
        const arrow = this.arrow;

        arrow.visible = false;
        box.setFilters(filters);
        pieces.setFilters(filters);
        pieces.pieces.forEach(it => it.topping.visible = true);
        pieces.moveXYFrom(prev.pieces.x, prev.pieces.y, 0.6, Ease.sineInOut);
        this.moveItemsFromLeft(box, () => {
            this.showArrow(this.arrow, this.arrowEnd);
            box.enabled = true;
            pieces.enabled = true;
            pieces.draggable = true;
            pieces.pulse();
            pieces.target = box;
            pieces.targetTransform = {
                y: box.y - pieces.height * 0.5
            };
            pieces.onStartDrag.add(() => {
                pieces.stopPulse();
            });
            pieces.onDragged.add(() => {
                this.hideArrow(this.arrow);
                box.enabled = false;
                pieces.enabled = false;
                for (let i = 0, n = Math.min(box.pieces.length, pieces.pieces.length); i < n; i++) {
                    const piece = pieces.pieces[i];
                    const boxPiece = box.pieces[i];
                    piece.visible = false;
                    piece.changeParent(box.visuals);
                    boxPiece.visible = true;
                    boxPiece.moveXYFrom(piece.x, piece.y, 0.4, Ease.sineInOut);
                    piece.changeParent(pieces.visuals);
                }
                this.delayedCall(0.6, onComplete);
            });
            pieces.onKeyboard.add(() => {
                Keyboard.focus(this.kitchen.buttonHome);
            });
            Keyboard.add(pieces);
            Keyboard.focus(pieces);
        }, 0.4);

        prev.destroy();
    }
}

class TurkishDelightStep7_Eat extends CookingActivityStep {

    readonly box = this.addChild(new Box());

    play(onComplete: Callback, prev: TurkishDelightStep6_Box): void {
        delightStep7.call(this);

        const box = this.box;
        const filters = prev.box.pieces[0].sprite.filters;
        const pieces = [
            [4, 6, 8, 9],
            [2, 5, 7],
            [0, 1, 3]
        ];
        const eatingVO = [
            "LetsGoLuna_174",
            "LetsGoLuna_175",
            "LetsGoLuna_176",
            "LetsGoLuna_177"
        ];
        HLF.ArrayUtils.shuffle(eatingVO);
        playVO("LetsGoLuna_022");

        box.setFilters(filters);
        box.pieces.forEach(it => it.visible = true);

        box.moveXYFrom(prev.box.x, prev.box.y, 0.6, Ease.sineInOut, () => {
            box.enabled = true;
            box.pulse();
            box.onClick.add(() => {
                AudioChannel.SFX.play("chewing");
                box.stopPulse();
                if (pieces.length > 0) {
                    pieces.shift()!.forEach(it => {
                        if (box.pieces[it]) box.pieces[it].alphaTo(0, 0.2);
                    });
                    if (pieces.length === 0) {
                        box.enabled = false;
                        Keyboard.focus(this.kitchen.buttonHome);
                        const vo = eatingVO.pop();
                        if (vo) {
                            playVO([...ReactionVO(), vo], onComplete);
                        } else {
                            onComplete();
                        }
                    }
                }
            });
            Keyboard.add(box);
            Keyboard.focus(box);
        });

        prev.destroy();
    }
}

class ColorBottle extends Ingredient {

    readonly colorIndex: number;

    constructor(color: string, colorIndex: number) {
        super(color);
        this.colorIndex = colorIndex;
    }
}

class Pieces extends CookingItem {

    readonly pieces = array(() => this.visuals.addChild(new Piece()));

    constructor() {
        super();
        this.draggable = false;
        this.enabled = false;
    }

    setFilters(filters: PIXI.Filter[]) {
        this.pieces.forEach(it => it.sprite.filters = filters);
    }
}

class MaskedPieces extends Pieces {

    readonly piecesMask: number[] = [];
}

class Jelly extends MaskedPieces {

    private piecesMaskPoly?: Poly;
    private maskDistance?: number;
    public slices = 0;

    onLayout() {
        this.pieces.forEach(it => it.visible = false);
        this.maskDistance = this.pieces[2].x - this.pieces[0].x;
    }

    setFilters(filters: PIXI.Filter[]) {
        super.setFilters(filters);
        this.sprite.filters = filters;
    }

    slice() {
        this.slices += 2;
        const lastSlice = this.slices >= this.pieces.length;
        if (this.piecesMaskPoly) {
            if (!lastSlice) {
                this.piecesMaskPoly.x += this.maskDistance!;
            }
        } else {
            this.piecesMaskPoly = this.visuals.addChild(new Poly(this.piecesMask));
            this.sprite.mask = this.piecesMaskPoly;
        }
        return !lastSlice;
    }
}


class Piece extends Container {

    readonly sprite = this.addChild(new Sprite());
    readonly topping = this.addChild(new Sprite());

    constructor() {
        super();
        this.topping.visible = false;
    }
}

class Box extends MaskedPieces {

    onLayout() {
        const mask = this.visuals.addChild(new Poly(this.piecesMask));
        for (const piece of this.pieces) {
            piece.visible = false;
            piece.mask = mask;
            piece.topping.visible = true;
        }
    }
}

class SugarBag extends CookingItem {
    readonly onVirtualDrag = Signal<(data: PIXI.InteractionData) => void>();
}

class SugarDrag extends Sprite {

    startDrag(p: IPoint) {
        this.x = p.x;
        this.y = p.y;
        this.alpha = 1;
        this.alphaFrom(0, 0.2);
    }
}